/** The words and addresses behind the Share dialog, kept out of the component
 *  so they can be tested as the plain functions they are.
 *
 * A session can be seen three ways (`private`, `link`, `public`), and the
 * dialog explains each one in the terms that matter to the person choosing:
 * who can open it, whether it is listed anywhere, and what happens to links
 * already sent. The post text is what a driver would type about the drive —
 * the time, the circuit, the car — and the two "post to" addresses are the
 * sites' own compose screens, opened with that text and the link filled in.
 */

import { lapTime } from '@/lib/format';
import type { DriveSession, SessionVisibility } from '@/lib/types';

/** What a session's reply says about who may see it. An older reply has no
 *  `visibility` and its `public` flag says the same thing less finely. */
export function visibilityOf(session: Pick<DriveSession, 'public' | 'visibility'>): SessionVisibility {
  return session.visibility ?? (session.public ? 'public' : 'private');
}

export interface VisibilityChoice {
  value: SessionVisibility;
  label: string;
  detail: string;
}

export const VISIBILITY_CHOICES: readonly VisibilityChoice[] = [
  {
    value: 'private',
    label: 'Private',
    detail: 'Only you. Making a shared session private ends every link to it; sharing it again makes a new one.',
  },
  {
    value: 'link',
    label: 'Anyone with the link',
    detail: 'Listed nowhere. Whoever has the link can open every lap of this drive without signing in.',
  },
  {
    value: 'public',
    label: 'Public',
    detail:
      'Anyone with the link, and listed for other drivers on this circuit as a lap to compare with.',
  },
];

/** The button that opens the dialog, saying where the session stands. */
export function shareButtonLabel(visibility: SessionVisibility): string {
  if (visibility === 'link') return 'Shared by link';
  if (visibility === 'public') return 'Public';
  return 'Share';
}

/** What a driver would say about the drive: `2:11.077 at Mount Panorama in a
 *  Porsche 911 GT3 R`. Whatever is unknown is left out rather than guessed. */
export function postText(
  session: Pick<DriveSession, 'track_name' | 'car' | 'laps'>,
  bestLapMs: number | null,
): string {
  const where = session.track_name ? ` at ${session.track_name}` : '';
  const car = session.car ? ` in a ${session.car}` : '';
  if (bestLapMs !== null && bestLapMs > 0) return `${lapTime(bestLapMs)}${where}${car}`;
  const laps = `${session.laps} ${session.laps === 1 ? 'lap' : 'laps'}`;
  return `${laps}${where}${car}`;
}

/** X's compose screen with the text and the link filled in. */
export function xPostUrl(url: string, text: string): string {
  const params = new URLSearchParams({ text, url });
  return `https://x.com/intent/post?${params.toString()}`;
}

/** Reddit's submit screen with the link and a title filled in; the driver
 *  picks the subreddit there. */
export function redditPostUrl(url: string, title: string): string {
  const params = new URLSearchParams({ url, title });
  return `https://www.reddit.com/submit?${params.toString()}`;
}
